import React, { useState, useMemo } from 'react';
import {
  Box, Grid, Typography, Paper, List, ListItem, ListItemText,
  ListItemIcon, Button, Divider, Avatar, Autocomplete, TextField,
  Chip, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Alert,
} from '@mui/material';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';
import PeopleIcon from '@mui/icons-material/People';
import HandshakeIcon from '@mui/icons-material/Handshake';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CampaignIcon from '@mui/icons-material/Campaign';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StatCard from '../../components/common/StatCard';
import DateRangePicker from '../../components/common/DateRangePicker';
import { cases, partners, campaigns } from '../../data/mockData';
import db from '../../data/demoDatabase';
import { PRODUCT_CATEGORIES } from '../../data/productConfigData';

const PRIMARY = '#1B365D';
const SECONDARY = '#00B894';

const monthlyTrends = [
  { month: 'Aug', referrals: 45, approvals: 28, commissions: 7200 },
  { month: 'Sep', referrals: 38, approvals: 22, commissions: 5800 },
  { month: 'Oct', referrals: 56, approvals: 35, commissions: 9400 },
  { month: 'Nov', referrals: 62, approvals: 41, commissions: 11200 },
  { month: 'Dec', referrals: 48, approvals: 30, commissions: 8600 },
  { month: 'Jan', referrals: 71, approvals: 45, commissions: 13500 },
  { month: 'Feb', referrals: 65, approvals: 40, commissions: 12100 },
];

const typeDistribution = [
  { name: 'Personal Financing', value: 35 },
  { name: 'Credit Card', value: 25 },
  { name: 'Housing Loan', value: 20 },
  { name: 'Auto Financing', value: 12 },
  { name: 'Property', value: 8 },
];

const PIE_COLORS = [PRIMARY, SECONDARY, '#FF6B6B', '#FFA726', '#AB47BC'];

const recentActivities = [
  { id: 1, action: 'New referral submitted', detail: 'Wong Mei Fen - Personal Financing-i via Siti Nurhaliza', time: '2 hours ago', color: '#1565C0' },
  { id: 2, action: 'Commission paid', detail: 'RM 4,500 to Tan Wei Ling for Housing Loan-i case', time: '5 hours ago', color: SECONDARY },
  { id: 3, action: 'Partner registered', detail: 'ProWealth Solutions Sdn Bhd - Pending approval', time: '1 day ago', color: '#F57F17' },
  { id: 4, action: 'Case approved', detail: 'Ahmad Faizal - Personal Financing-i approved by Al Rajhi Bank', time: '1 day ago', color: '#2E7D32' },
  { id: 5, action: 'QR Code generated', detail: 'Raya 2025 Special campaign - Ong Wealth Advisory', time: '2 days ago', color: '#7B1FA2' },
];

const AVAILABLE_METRICS = [
  { key: 'leads', label: 'Lead Count', color: '#1B365D' },
  { key: 'approvalRatio', label: 'Approval Ratio (%)', color: '#00B894' },
  { key: 'productCount', label: 'Product Variety', color: '#E65100' },
  { key: 'commission', label: 'Commission (RM)', color: '#7B1FA2' },
];

const APPROVED_STATUSES = ['Approved', 'Disbursed'];

// Compute metrics for a single referrer within a date range
function computeMetricsForUser(userId, allCases, startMs, endMs) {
  const ownCases = allCases.filter((c) => {
    if (c.referralOwnerId !== userId) return false;
    const t = new Date(c.createdAt).getTime();
    return t >= startMs && t <= endMs;
  });
  const total = ownCases.length;
  const approved = ownCases.filter((c) => APPROVED_STATUSES.includes(c.status)).length;
  const ratio = total === 0 ? 0 : Math.round((approved / total) * 100);
  const productCount = new Set(ownCases.map((c) => c.productName)).size;
  const commission = ownCases.reduce((sum, c) => sum + (c.commissionAmount || 0), 0);
  return { leads: total, approvalRatio: ratio, productCount, commission };
}

// Compute product performance per category
function computeProductPerformance(categoryKey, allCases) {
  // Map our 7 categories to mockData product names/bank patterns
  const categoryMap = {
    personal_loan: ['Personal Financing', 'Personal Loan'],
    credit_card: ['Credit Card'],
    housing_loan: ['Housing Loan', 'Home Financing'],
    property: ['Property'],
    business_loan: ['Business Loan', 'SME'],
    government_loan: ['Government', 'Civil Servant'],
    micro_loan: ['Micro'],
  };
  const keywords = categoryMap[categoryKey] || [];
  const matching = allCases.filter((c) =>
    keywords.some((kw) => (c.productName || '').toLowerCase().includes(kw.toLowerCase()))
  );
  // Group by bank (lender)
  const byBank = {};
  matching.forEach((c) => {
    const bank = c.bank || 'Unknown';
    if (!byBank[bank]) byBank[bank] = { bank, count: 0, totalAmount: 0, approved: 0 };
    byBank[bank].count += 1;
    byBank[bank].totalAmount += (c.amount || 0);
    if (APPROVED_STATUSES.includes(c.status)) byBank[bank].approved += 1;
  });
  return Object.values(byBank)
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);
}

export default function AdminDashboard() {
  const totalReferrals = cases.length;
  const activePartners = partners.filter((p) => p.status === 'active').length;
  const pendingApprovals = cases.filter((c) => c.status === 'Submitted' || c.status === 'In Review').length;
  const totalCommission = partners.reduce((sum, p) => sum + p.totalCommission, 0);

  // Data from live database (mutable)
  const allDbUsers = useMemo(() => db.users.getAll(), []);
  const allDbCases = useMemo(() => db.cases.getAll(), []);

  // Referrers = partners + individual agents
  const referrers = useMemo(
    () => allDbUsers.filter((u) => ['partner', 'individual_agent'].includes(u.role)),
    [allDbUsers]
  );

  // Performance Comparison state
  const [selectedReferrers, setSelectedReferrers] = useState(
    () => referrers.slice(0, 4)
  );
  const [selectedMetrics, setSelectedMetrics] = useState(
    [AVAILABLE_METRICS[0], AVAILABLE_METRICS[1], AVAILABLE_METRICS[3]]
  );
  const defaultStart = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  }, []);
  const defaultEnd = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [compStart, setCompStart] = useState(defaultStart);
  const [compEnd, setCompEnd] = useState(defaultEnd);

  // Chart data for performance comparison
  const comparisonData = useMemo(() => {
    const startMs = new Date(compStart).getTime();
    const endMs = new Date(compEnd + 'T23:59:59').getTime();
    return selectedReferrers.map((r) => {
      const metrics = computeMetricsForUser(r.id, allDbCases, startMs, endMs);
      return {
        name: r.name.length > 15 ? r.name.slice(0, 15) + '...' : r.name,
        fullName: r.name,
        id: r.id,
        ...metrics,
      };
    });
  }, [selectedReferrers, compStart, compEnd, allDbCases]);

  // Product Performance state
  const [productCategoryTab, setProductCategoryTab] = useState(0);
  const productPerformanceData = useMemo(() => {
    const categoryKey = PRODUCT_CATEGORIES[productCategoryTab]?.key;
    if (!categoryKey) return [];
    return computeProductPerformance(categoryKey, allDbCases);
  }, [productCategoryTab, allDbCases]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, color: PRIMARY, mb: 3 }}>
        Admin Dashboard
      </Typography>

      {/* Stat Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Referrals"
            value={totalReferrals}
            icon={PeopleIcon}
            trend="up"
            trendValue="+12%"
            subtitle="vs last month"
            color={PRIMARY}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Partners"
            value={activePartners}
            icon={HandshakeIcon}
            trend="up"
            trendValue="+3"
            subtitle="this month"
            color={SECONDARY}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Pending Approvals"
            value={pendingApprovals}
            icon={PendingActionsIcon}
            trend="down"
            trendValue="-5"
            subtitle="vs last week"
            color="#FFA726"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Commission (RM)"
            value={`RM ${totalCommission.toLocaleString()}`}
            icon={AttachMoneyIcon}
            trend="up"
            trendValue="+18%"
            subtitle="vs last month"
            color="#2E7D32"
          />
        </Grid>
      </Grid>

      {/* Charts Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Line Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY, mb: 2 }}>
              Monthly Referral Trends
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={monthlyTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="referrals"
                  stroke={PRIMARY}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Referrals"
                />
                <Line
                  type="monotone"
                  dataKey="approvals"
                  stroke={SECONDARY}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  name="Approvals"
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Pie Chart */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY, mb: 2 }}>
              Referral Type Distribution
            </Typography>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={typeDistribution}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={80}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {typeDistribution.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <Box sx={{ mt: 1 }}>
              {typeDistribution.map((item, idx) => (
                <Box key={item.name} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <FiberManualRecordIcon sx={{ fontSize: 10, color: PIE_COLORS[idx] }} />
                  <Typography variant="caption" color="text.secondary">
                    {item.name} ({item.value}%)
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Bottom Row: Recent Activities & Quick Actions */}
      <Grid container spacing={3}>
        {/* Recent Activities */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY, mb: 2 }}>
              Recent Activities
            </Typography>
            <List disablePadding>
              {recentActivities.map((activity, idx) => (
                <React.Fragment key={activity.id}>
                  <ListItem sx={{ px: 0, py: 1.5 }}>
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <Avatar sx={{ width: 32, height: 32, bgcolor: `${activity.color}20`, color: activity.color }}>
                        <FiberManualRecordIcon sx={{ fontSize: 12 }} />
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {activity.action}
                        </Typography>
                      }
                      secondary={activity.detail}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 2 }}>
                      {activity.time}
                    </Typography>
                  </ListItem>
                  {idx < recentActivities.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', height: '100%' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, color: PRIMARY, mb: 3 }}>
              Quick Actions
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<CampaignIcon />}
                fullWidth
                sx={{
                  bgcolor: PRIMARY, py: 1.5, borderRadius: 2, textTransform: 'none',
                  fontWeight: 600, '&:hover': { bgcolor: '#142847' },
                }}
              >
                Create Campaign
              </Button>
              <Button
                variant="contained"
                startIcon={<QrCode2Icon />}
                fullWidth
                sx={{
                  bgcolor: SECONDARY, py: 1.5, borderRadius: 2, textTransform: 'none',
                  fontWeight: 600, '&:hover': { bgcolor: '#00a383' },
                }}
              >
                Generate QR Code
              </Button>
              <Button
                variant="outlined"
                startIcon={<HowToRegIcon />}
                fullWidth
                sx={{
                  borderColor: PRIMARY, color: PRIMARY, py: 1.5, borderRadius: 2,
                  textTransform: 'none', fontWeight: 600,
                  '&:hover': { borderColor: PRIMARY, bgcolor: `${PRIMARY}08` },
                }}
              >
                Approve Partners
              </Button>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: PRIMARY, mb: 1.5 }}>
              Active Campaigns
            </Typography>
            {campaigns
              .filter((c) => c.status === 'active')
              .slice(0, 3)
              .map((camp) => (
                <Box key={camp.id} sx={{ mb: 1.5, p: 1.5, bgcolor: '#F5F7FA', borderRadius: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {camp.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {camp.totalLeads} leads | {camp.conversions} conversions
                  </Typography>
                </Box>
              ))}
          </Paper>
        </Grid>
      </Grid>

      {/* ─── Performance Comparison ────────────────────────────── */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY }}>
            Performance Comparison
          </Typography>
          <Chip label={`${selectedReferrers.length}/10 selected`} size="small"
            sx={{ fontWeight: 600, backgroundColor: '#E3F2FD', color: '#1565C0' }} />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Compare partner and agent performance across multiple metrics and date ranges
        </Typography>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={5}>
            <Autocomplete
              multiple size="small"
              options={referrers}
              value={selectedReferrers}
              onChange={(_, newVal) => {
                if (newVal.length <= 10) setSelectedReferrers(newVal);
              }}
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderTags={(value, getTagProps) =>
                value.map((opt, index) => (
                  <Chip
                    variant="outlined" label={opt.name} size="small"
                    {...getTagProps({ index })}
                    sx={{
                      backgroundColor: opt.role === 'partner' ? '#E3F2FD' : '#F3E5F5',
                      color: opt.role === 'partner' ? '#1565C0' : '#7B1FA2',
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Select Partners / Agents (max 10)"
                  placeholder={selectedReferrers.length === 0 ? 'Select users to compare' : ''} />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <Autocomplete
              multiple size="small"
              options={AVAILABLE_METRICS}
              value={selectedMetrics}
              onChange={(_, newVal) => {
                if (newVal.length <= 4) setSelectedMetrics(newVal);
              }}
              getOptionLabel={(opt) => opt.label}
              isOptionEqualToValue={(a, b) => a.key === b.key}
              renderTags={(value, getTagProps) =>
                value.map((opt, index) => (
                  <Chip variant="outlined" label={opt.label} size="small"
                    {...getTagProps({ index })}
                    sx={{ backgroundColor: `${opt.color}15`, color: opt.color, fontWeight: 600 }} />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Metrics (max 4)" />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <DateRangePicker
              value={{ start: compStart, end: compEnd }}
              onChange={({ start, end }) => { setCompStart(start); setCompEnd(end); }}
            />
          </Grid>
        </Grid>

        {selectedReferrers.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select at least one partner/agent to compare performance.
          </Alert>
        ) : selectedMetrics.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select at least one metric to display.
          </Alert>
        ) : (
          <>
            {/* Chart */}
            <Box sx={{ mb: 3 }}>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={comparisonData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} angle={-15} textAnchor="end" />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {selectedMetrics.map((m) => (
                    <Bar key={m.key} dataKey={m.key} name={m.label} fill={m.color} radius={[4, 4, 0, 0]} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </Box>

            {/* Comparison table */}
            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#F5F7FA' }}>
                    <TableCell sx={{ fontWeight: 700, color: PRIMARY }}>Partner / Agent</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: PRIMARY }}>Role</TableCell>
                    {selectedMetrics.map((m) => (
                      <TableCell key={m.key} align="right" sx={{ fontWeight: 700, color: m.color }}>
                        {m.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {comparisonData.map((row) => {
                    const referrer = referrers.find((r) => r.id === row.id);
                    return (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{row.fullName}</TableCell>
                        <TableCell>
                          <Chip label={referrer?.role === 'partner' ? 'Partner' : 'Agent'} size="small"
                            sx={{
                              fontSize: '0.7rem',
                              backgroundColor: referrer?.role === 'partner' ? '#E3F2FD' : '#F3E5F5',
                              color: referrer?.role === 'partner' ? '#1565C0' : '#7B1FA2',
                              fontWeight: 600,
                            }} />
                        </TableCell>
                        {selectedMetrics.map((m) => (
                          <TableCell key={m.key} align="right" sx={{ fontWeight: 700 }}>
                            {m.key === 'commission'
                              ? `RM ${(row[m.key] || 0).toLocaleString()}`
                              : m.key === 'approvalRatio'
                              ? `${row[m.key]}%`
                              : row[m.key]}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      {/* ─── Product Performance ───────────────────────────────── */}
      <Paper sx={{ p: 3, borderRadius: 3, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', mt: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: PRIMARY, mb: 0.5 }}>
          Product Performance
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Top 5 lenders by number of cases and disbursement, per product category
        </Typography>

        <Tabs value={productCategoryTab} onChange={(_, v) => setProductCategoryTab(v)}
          variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: '1px solid #E0E0E0', mb: 3,
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
          {PRODUCT_CATEGORIES.map((cat) => (
            <Tab key={cat.key} label={cat.label} />
          ))}
        </Tabs>

        {productPerformanceData.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            No cases yet for {PRODUCT_CATEGORIES[productCategoryTab]?.label}. Try another category.
          </Alert>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}>
                Top 5 Lenders — Case Count
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={productPerformanceData} layout="vertical" margin={{ top: 5, right: 20, left: 40, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E0E0E0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="bank" tick={{ fontSize: 11 }} width={120} />
                  <Tooltip />
                  <Bar dataKey="count" name="Cases" fill={PRODUCT_CATEGORIES[productCategoryTab]?.color || PRIMARY} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} md={5}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}>
                Ranking & Totals
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#F5F7FA' }}>
                      <TableCell sx={{ fontWeight: 700, color: PRIMARY }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: PRIMARY }}>Lender</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: PRIMARY }}>Cases</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: PRIMARY }}>Total Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {productPerformanceData.map((r, idx) => (
                      <TableRow key={r.bank} hover>
                        <TableCell>
                          <Chip label={`#${idx + 1}`} size="small"
                            sx={{
                              fontSize: '0.7rem', fontWeight: 700,
                              backgroundColor: idx === 0 ? '#FFF8E1' : '#ECEFF1',
                              color: idx === 0 ? '#F57F17' : '#546E7A',
                            }} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{r.bank}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>{r.count}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          RM {(r.totalAmount || 0).toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </Paper>
    </Box>
  );
}
